"use client";

import { useEffect, useId, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const EXIT_MS = 220;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** Закрытие по клику на затемнение (панель модалки не закрывает) */
  closeOnBackdrop?: boolean;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  closeOnBackdrop = false,
}: ModalProps) {
  const titleId = useId();
  const [isPresent, setIsPresent] = useState(open);
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    if (open) setIsPresent(true);
  }, [open]);

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setIsVisible(false);
    const t = window.setTimeout(() => setIsPresent(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!isPresent || !isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPresent, isVisible, onClose]);

  if (!isPresent || typeof document === "undefined") return null;

  const max =
    size === "sm"
      ? "max-w-sm"
      : size === "lg"
        ? "max-w-lg"
        : size === "xl"
          ? "max-w-2xl"
          : "max-w-md";

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4 sm:p-6",
        "bg-black/40 transition-opacity duration-200 ease-out motion-reduce:duration-0",
        isVisible ? "opacity-100" : "opacity-0",
      )}
      onClick={closeOnBackdrop && isVisible ? onClose : undefined}
      role={closeOnBackdrop ? "presentation" : undefined}
    >
      <div
        className={cn(
          "my-auto w-full max-h-[min(90dvh,calc(100vh-2rem))] overflow-y-auto overscroll-contain rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900",
          "transform-gpu transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:duration-0",
          isVisible ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.97] opacity-0",
          max,
        )}
        onClick={closeOnBackdrop ? (e) => e.stopPropagation() : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="mb-5 flex shrink-0 items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
